import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface Comment {
  id: string;
  taskId: string;
  userId: string;
  content: string;
  parentId: string | null;
  mentions: string[];
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SupabaseCommentRow {
  id: string;
  task_id: string;
  user_id: string;
  content: string;
  parent_id: string | null;
  mentions: string[];
  edited_at: string | null;
  created_at: string;
  updated_at: string;
}

function convert(row: SupabaseCommentRow): Comment {
  return {
    id: row.id,
    taskId: row.task_id,
    userId: row.user_id,
    content: row.content,
    parentId: row.parent_id,
    mentions: row.mentions || [],
    editedAt: row.edited_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/** Fetch all comments for a task, ordered by creation time */
export async function fetchComments(taskId: string): Promise<Comment[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('comments')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true })
  );
  if (error) throw error;
  return (data || []).map(convert);
}

/** Create a new comment */
export async function createComment(opts: {
  taskId: string;
  userId: string;
  content: string;
  parentId?: string | null;
  mentions?: string[];
}): Promise<Comment> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('comments')
      .insert({
        task_id: opts.taskId,
        user_id: opts.userId,
        content: opts.content,
        parent_id: opts.parentId || null,
        mentions: opts.mentions || [],
      })
      .select()
      .single()
  );
  if (error || !data) throw error || new Error('Failed to create comment');
  return convert(data);
}

/** Update a comment's content */
export async function updateComment(
  id: string,
  content: string,
  mentions?: string[],
): Promise<Comment> {
  const payload: Record<string, unknown> = {
    content,
    edited_at: new Date().toISOString(),
  };
  if (mentions !== undefined) payload.mentions = mentions;

  const { data, error } = await withRetry(() =>
    supabase
      .from('comments')
      .update(payload)
      .eq('id', id)
      .select()
      .single()
  );
  if (error || !data) throw error || new Error('Failed to update comment');
  return convert(data);
}

/** Delete a comment */
export async function deleteComment(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('comments').delete().eq('id', id)
  );
  if (error) throw error;
}

/** Extract @mention user IDs from content (format: @[userId]) */
export function extractMentions(content: string): string[] {
  const regex = /@\[([^\]]+)\]/g;
  const mentions: string[] = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (!mentions.includes(match[1])) mentions.push(match[1]);
  }
  return mentions;
}
